import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { LiveMatchStoreProvider } from "@/hooks/useLiveMatchStore";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorFallback } from "@/components/ErrorFallback";
import { UsernameGate } from "@/components/auth/UsernameGate";
import { DynamicFaviconProvider } from "@/components/DynamicFaviconProvider";
import { ScrollToTop } from "@/components/ScrollToTop";

// Critical: Load Index eagerly for homepage performance
import Index from "./pages/Index";

// Lazy load all other pages for bundle splitting
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const Rewards = lazy(() => import("./pages/Rewards"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const DataAccuracy = lazy(() => import("./pages/DataAccuracy"));
const CMSPage = lazy(() => import("./pages/CMSPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

// SEO Landing Pages - lazy load
const PredictionsToday = lazy(() => import("./pages/seo/PredictionsToday"));
const BettingTips = lazy(() => import("./pages/seo/BettingTips"));
const AIInsights = lazy(() => import("./pages/seo/AIInsights"));
const LeaguePredictions = lazy(() => import("./pages/seo/LeaguePredictions"));

// Daily Coupons - lazy load
const Coupons = lazy(() => import("./pages/Coupons"));

// Best Betting Sites - lazy load
const BestBettingSites = lazy(() => import("./pages/BestBettingSites"));

// Lazy load Google Analytics (non-critical, loads after interaction)
const GoogleAnalytics = lazy(() => import("./components/GoogleAnalytics").then(m => ({ default: m.GoogleAnalytics })));

// Configure QueryClient with global error handling and retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && error.message.includes('40')) {
          return false;
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30 * 1000, // 30 seconds default
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Minimal loading fallback for Suspense
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  );
}

// Global app error fallback
function AppErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <ErrorFallback
        type="generic"
        title="Something went wrong"
        message="The application encountered an unexpected error. Please refresh the page to try again."
        onRetry={() => window.location.reload()}
        showHomeLink
      />
    </div>
  );
}

const App = () => (
  <ErrorBoundary fallback={<AppErrorFallback />} componentName="App">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          {/* LiveMatchStoreProvider: Single source of truth for all live match data */}
          <LiveMatchStoreProvider>
            {/* DynamicFaviconProvider: Updates favicon based on branding settings */}
            <DynamicFaviconProvider />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              {/* ScrollToTop: Ensures pages load from top and handles anchor navigation */}
              <ScrollToTop />
              {/* Google Analytics - lazy loaded after page interactive */}
              <Suspense fallback={null}>
                <GoogleAnalytics />
              </Suspense>
              {/* UsernameGate: Shows onboarding modal for users without username */}
              <UsernameGate>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/match/:slug" element={<MatchDetail />} />
                    <Route path="/rewards" element={<Rewards />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/data-accuracy" element={<DataAccuracy />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    
                    {/* SEO Landing Pages */}
                    <Route path="/predictions/today" element={<PredictionsToday />} />
                    <Route path="/betting-tips" element={<BettingTips />} />
                    <Route path="/ai-insights" element={<AIInsights />} />
                    <Route path="/predictions/:league" element={<LeaguePredictions />} />
                    
                    {/* Daily Coupons */}
                    <Route path="/coupons" element={<Coupons />} />
                    <Route path="/coupons/:date" element={<Coupons />} />
                    
                    {/* Best Betting Sites - SEO Landing Page */}
                    <Route path="/best-betting-sites" element={<BestBettingSites />} />
                    
                    {/* CMS Pages */}
                    <Route path="/privacy-policy" element={<CMSPage />} />
                    <Route path="/terms-of-service" element={<CMSPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </UsernameGate>
            </BrowserRouter>
          </LiveMatchStoreProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
